import { tt } from '@agos/shared'

/**
 * The rights the customer directory's actions ask - `FRONTEND.md` §9.758, `BACKEND.md` §10A.413 (owner, folded into
 * O119).
 *
 * <p>Three actions on *Zákazníci* reached a main-administrator-only endpoint: the single delete asked
 * `systemRole === 'main_admin'`, and the bulk bar's *Email* and *Anonymizovať* asked nothing at all, so every visitor
 * was offered a send and an irreversible anonymisation the server refused. §10A.413 opens all three to a right, the
 * write key alone - `marketing_customers`, the screen's own key, is never enough - so each control asks its right
 * through `hasPermission`, which is true for the main administrator and otherwise reads the person's rights.
 *
 * <p><b>What stays as it was</b>: *SMS*, *Push* and *Body* in the same bar are `{main_admin, admin}` on the server and
 * outside §10A.413; and the composer never sends an empty recipient list (`canSend` asks `recipients > 0`), which is
 * the one shape of `POST /customers/send-email` that stays the main administrator's - it mails every consenting
 * member.
 */
export const MARKETING_CUSTOMERS_EMAIL = 'marketing_customers_email'
export const MARKETING_CUSTOMERS_ANONYMIZE = 'marketing_customers_anonymize'
export const MARKETING_CUSTOMERS_DELETE = 'marketing_customers_delete'
/**
 * §9.787, owner O140 - banning a customer and the whole blocklist. The right the catalogue has always described as
 * *"zákazník sa nemôže prihlásiť, dáta ostávajú"* and no endpoint read until BACKEND.md §10A.439.
 */
export const MARKETING_CUSTOMERS_DEACTIVATE = 'marketing_customers_deactivate'

/** Which of the three actions this person is offered. */
export interface CustomerActions {
  /** *Email* in the bulk bar and in the drawer - `POST /customers/send-email`. */
  email: boolean
  /** *Anonymizovať* in the bulk bar - `POST /customers/bulk-anonymize` and its poll. */
  anonymize: boolean
  /** *Zmazať zákazníka* in the drawer - `DELETE /customers/{id}`. */
  delete: boolean
  /** §9.787 - *Zablokovať zákazníka* in the drawer - `POST /customers/{id}/block`. */
  block: boolean
}

/** One rule for the bar and the drawer, asked of the session's own reader, so the two cannot come to answer differently. */
export function customerActionsFor(hasPermission: (permission: string) => boolean): CustomerActions {
  return {
    email: hasPermission(MARKETING_CUSTOMERS_EMAIL),
    anonymize: hasPermission(MARKETING_CUSTOMERS_ANONYMIZE),
    delete: hasPermission(MARKETING_CUSTOMERS_DELETE),
    block: hasPermission(MARKETING_CUSTOMERS_DEACTIVATE),
  }
}

/**
 * Why the drawer's *E-mail* is not offered - its tooltip, the way the drawer already says why a channel cannot be used.
 * The right under the roles screen's own label: what the person needs is a tick, not a person to ask.
 */
export function emailRefusal(): string {
  return `${tt('Na e-mail zákazníkovi je potrebné právo')} „${tt('Zákazníci - E-mail')}“.`
}
